import { createApi, retry } from '@reduxjs/toolkit/query/react';
import { AxiosProgressEvent } from 'axios';
import { ROLE } from '../components/common';
import { Language, OffsetLimit, StatusPagesResponse, StatusResponse } from './api';
import { FileInfo, SourcesType } from './content_v1';
import { baseQueryWithReauth } from './reauth';


type TClass = 'tariff' | 'option';

export enum AccountResType {
    SITE = 0, // 'Владельцы сайта'
    PROVIDER = 1, // 'Поставщик'
    CONSUMER = 2, // 'Потребитель'
    PARTNER = 3, // 'Партнер'
    PARTNER_MODERATOR = 4, // 'Партнер модератор'
    PARTNER_TVSRC = 6, // 'Партнер источник'
    PARTNER_TVSRC_MDLG = 5, // 'Партнер источник Медиалогистика'
    GUEST = 7, // Гость
}

export enum AccessRequestStatus {
    // OPEN = 'open',
    // PENDING = 'pending',
    // GRANTED = 'granted',
    // REJECTED = 'rejected',
    // ARCHIVED = 'archived',
    OPEN = 0, // 'open'
    GRANTED = 1, // 'granted'
    REJECTED = 2, // 'rejected'
    PENDING = 3, // 'pending'
    CANCELED = 4, // 'canceled'
    REDIRECTED = 5, // 'redirected'
    COPIED = 6, // 'copied'
    CLOSED = 7, // 'closed'
    FORWARDED = 8, // 'forwarded'
    ACTIVE = 99, // 'active',
    ARCHIVED = 100, // 'archived',
}

export enum CounterUnit {
    UNITS = 1, // _("CONST-009001", "Единицы")
    BYTES = 2, // _("CONST-009002", "Байты")
    ONOFF = 3, // _("CONST-009003", "Вкл/Выкл")
    STR = 4, // _("CONST-009004", "Строка")
}

export interface AclGroup {
    id: number,
    group: string,
    accounts: AccountAvail[]
}

export interface AuthResponse {
    username: string
    access: string
    refresh: string
    pd_agreement: boolean,
    agr_offer: boolean,
    accounts?: AccountShort[],
    suUser?: string
    suRefresh?: string
    signing: string
}

export interface AccountListElement {
    id: number,
    rtype: AccountResType,
    name: string,
    nickname: string | null,
    org: OrgMinimal,
    owner: UserProfileMinimal,
    logo: string | null,
    reliable: ReliableType,
    agr_offer: boolean,
}

export interface AccountUnit {
    id: number,
    rtype: AccountResType,
    name: string,
    nickname: string | null,
    org: {
        id: number,
        name: string
    } | null,
    owner: UserProfile | null,
    logo: string | null,
    aclgroups: AclGroup[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    additional: { [key: string]: any },
    is_favorites: boolean,
    reliable: ReliableType,
}

export interface AccountClientUnit extends AccountUnit {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    additional: { [key: string]: any },
    users?: UserProfileAccount[],
}

export interface AccountShort {
    id: number,
    rtype: AccountResType,
    name: string,
    orgname?: string,
    logo: string | null
}

export interface AccountReg {
    rtype: number | null,
    name: string,
    agreement: string | null,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    additional: any
}

export interface AccountAvail {
    id: number,
    rtype: AccountResType,
    name: string,
    nickname: string | null,
    org: OrgMinimal | null,
    owner: UserProfileMinimal | null,
    logo: string | null
}

export interface AccountLogin {
    id: number,
    rtype: AccountResType,
    name: string,
    nickname: string | null,
    logo: string | null,
    orgname: string | null,
    nickid: string | null,
    roles: {
        id: number,
        name: string
    }[]
}

export interface ApproveRequest {
    id?: number,
    org: {
        id?: number,
        ctype?: number | null,
        name?: string,
    },
    account: {
        rtype?: AccountResType | string,
        name: string,
        email: string,
        nickname?: string,
        logo: string | null
    },
    user: {
        username: string,
        email: string,
        lang?: Language,
        hide_fio?: boolean,
        hide_phone?: boolean,
        hide_email?: boolean,
        hide_birthday?: boolean,
    }
}

export interface OrgMinimal {
    id: number,
    name: string,
    taxnum: string | null,
    reliable: ReliableType,
}

export interface OrganizationShort extends OrgMinimal {
    ctype: number,
    legalnm: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    details: any
}

export interface OrganizationContract {
    id: number,
    label: string,
    name: string,
    param: { 'warn': boolean }
}

export interface Organization extends OrganizationShort {
    created?: string | null
    updated?: string | null
    mbcontract?: OrganizationContract | number | null,
    files: FileInfo[] | number[],
}

export interface OrganizationFull extends Organization {
    accounts: {
        id: number,
        rtype: number,
        name: string,
        nickname: string | null,
        org: OrgMinimal,
        owner: UserProfileMinimal,
        logo: string | null
    }[],
}

export interface Dataplan {
    name: string
}

export interface Role {
    id: number
    label: ROLE
    name: string
    superAdmin: boolean,
    displayname?: string
    ts?: string
}

export interface Tag {
    id: number,
    name: string,
    comment: string
    accounts: AccountShort[]
}

export interface TagBase {
    id: number,
    name: string,
    hashname: string | null,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    additional: { [key: string]: any },
}

export interface TagMinimal extends TagBase {
    control: boolean
}

export interface TagListElement extends TagBase {
    accounts: (AccountShort & { control: boolean })[],
}

export interface TagRequest {
    id?: number
    name: string,
    accounts: number[],
    hashname: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    additional: { [key: string]: any },
    control?: string
}

export interface TagFull extends TagListElement {
    users?: UserProfileAccount[],
}

export interface RelatedOrganization {
    id: number,
    active: boolean,
    org: OrganizationShort
    orgacc: AccountShort,
    reason: string,
    validity: {
        lower: null,
        upper: null,
        bounds: string
    },
    acr_flag: boolean | null,
    added_by: number
}

export interface RelatedOrganizationRequest {
    id: number,
    body: {
        org?: number
        acr_flag?: boolean
        active?: boolean
        reason?: string,
        validity?: {
            bounds: string
        },
    }
}

export interface AccountRequest {
    name: string,
    org: number,
    emails: string[],
    extowner?: number,
    owner?: {
        email: string,
        firstname: string,
        surname?: string,
        lastname: string,
        phone: string,
    },
    rtype: number,
    tariff: number,
    visibility?: ResGrpVisibility,
}

export interface AccountUpdateRequest {
    id: number,
    nickname: string | null,
    org: number,
    owner: number | null,
    send_emails: boolean,
    emails: string[],
    send_sms: boolean,
    phones: string[],
    send_telegrams: boolean,
    telegrams: number[],
    rtype: number,
    name: string,
    agreement: string | null,
    agr_offer: boolean | null,
    flags: number,
    email: string | null,
    phone: string | null,
    contact: string | null,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    additional: { [key: string]: any },
    sources: SourcesType[],
    lang?: Language,
    country?: number | null,
    visibility?: ResGrpVisibility,
    rfo: ReliableSettingsType,
    reliable?: ReliableType,
}

export interface Country {
    id: number,
    label: string,
    rank: number,
    terms: {
        en: string,
        ru: string,
        default: string
    },
    param: null
}

export interface OTPResponse {
    issuer: string // - Название сервиса в Яндекс.Ключе
    secret: string // - Секретный ключ
    qrcode: string // "data:image/png;base64,....." - QR-код, который можно в Яндекс.Ключе отсканировать, чтобы ничего вручную не вводить
}

export interface OTPStatus {
    otp_enabled: boolean, // - Вкл/Выкл
    otp_configured: boolean, // - Настроена
    otp_secretset: boolean, // - Выполнена генерация секретного ключа (через POST)
    issuer: string // - Название сервиса для приложений
}

export interface Validity {
    lower: string | null,
    upper: string | null,
    bounds?: string
}

export interface TariffOption {
    id: number,
    label: string,
    name: string,
    group?: TariffGroup,
    dependencies: [],
    included: boolean,
    additional: boolean,
    value: number,
    units: CounterUnit,
    option_id: number,
}

export interface Tariff {
    id: number,
    label: string,
    name: string,
    added: string,
    validity: {
        lower: string | null,
        upper: string | null,
        bounds: string
    },
    enabled: boolean,
    price: string,
    options: TariffOption[],
}

export interface TariffAccountShort {
    id: number,
    tariff_id: number,
    label: string,
    name: string,
    validity: {
        lower: string | null,
        upper: string | null,
        bounds: string
    },
    active: true
}

export interface TariffAccount {
    id: number,
    tariff_id: number,
    account_id: number,
    label: string,
    name: string,
    validity: {
        lower: string | null,
        upper: string | null,
        bounds: string
    },
    active: boolean,
    options: TariffAccountOption[],
}

export interface TariffGroup {
    id: number,
    label: string,
    name: string,
}

export interface TariffAccountOption {
    id: number,
    option_id: number,
    label: string,
    name: string,
    group: TariffGroup
    value: number,
    units: CounterUnit,
    validity: Validity,
    active: boolean,
}

export interface TariffTask {
    id: number,
    tariff: {
        id: number,
        label: string,
        name: string
    } | null,
    option: {
        id: number,
        label: string,
        name: string,
        value: number,
        units: CounterUnit,
    } | null,
    etime: string
}

export interface TariffHistory {
    tclass: TClass,
    tariff: number | null,
    option: number | null,
    label: string,
    name: string,
    validity: Validity,
    user: UserProfileMinimal | null
}

export enum ResGrpVisibility {
    HIDDEN = 1, // 'Скрытый аккаунт'
    PUBLIC = 2, // 'Публичный аккаунт'
}

export enum ReliableType {
    NOTSET = 0, // "Не установлено"
    RELIABLE = 1, // "Надежный партнер"
}

export enum ReliableSettingsType {
    ORG = 0, // "Из карточки организации"
    ACCOUNT = 1, // "Из карточки аккаунта"
}

export interface AccountDisk {
    id: number,
    bucket: string,
    quota_exceeded: boolean,
    size: number,
    used: number,
    expire: string,
}

export interface AccountDiskWithOwner extends AccountDisk {
    owner: AccountListElement,
}

export interface AccountResponse {
    id: number,
    rtype: AccountResType,
    name: string,
    nickname: string | null,
    org: OrgMinimal,
    owner: UserProfile,
    logo: string | null,
    agreement: string | null,
    agr_offer: boolean | null,
    tags: TagMinimal[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    additional: { [key: string]: any },
    send_emails: boolean,
    emails: string[],
    send_telegrams: boolean,
    telegrams: TgUser[],
    users: UserProfileAccount[],
    created: string | null,
    updated: string | null,
    flags: number,
    lang: Language,
    tariffs: {
        tariffs: TariffAccount[],
        options: TariffAccountOption[],
        tasks: TariffTask[],
    },
    sources: { id: number, name: string, param: { stype: SourcesType, info: { org: { en: string, ru: string } }, partner_types: number[] } }[] | null,
    country: { id: number, label: string, name: string } | null,
    visibility: ResGrpVisibility,
    reliable: ReliableType,
    disks: AccountDiskWithOwner[],
    owned_disks: AccountDisk[],
}

export interface Registration {
    org: {
        id?: number,
        name?: string
    },
    user: {
        email: string,
        firstname?: string | null,
        lastname?: string | null,
        surname?: string | null,
        phone?: string | null,
        birthday?: string | null,
        lang?: Language,
        hide_fio?: boolean,
        hide_phone?: boolean,
        hide_email?: boolean,
        hide_birthday?: boolean,
        nickid?: string | null
        subscription?: boolean,
        pd_agreement?: boolean,
    },
    id: number,
    email: string,
    account: AccountReg,
    roles: string[],
    email_verification: string,
    last_updated: string
}

export interface RegistrationRequest {
    email: string,
    profile: {
        company: string,
        company_typ?: number,
        firstname: string,
        surname?: string,
        lastname?: string,
        phone: string,
        accountname?: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        additional?: any
    },
    roles: string[],
    email_verification: string,
    lang: 'ru' | 'en'
}

export interface TgUser {
    user_id: number,
    tg_userlang: string,
    tg_username: string | null,
    tg_userlastname: string | null,
    tg_userfirstname: string | null,
}

export interface TVDBAccount {
    id: number,
    yellid: string,
    info: {
        taxnum: number,
        orgname: string
    },
    accounts: AccountListElement[],
}

export interface TVDBAccountRequest {
    id: number,
    yellid: string,
    info: {
        taxnum: number,
        orgname: string
    },
    accounts: number[],
}

export interface UserProfileMinimal {
    id?: number,
    email: string,
    username: string,
    phone: string,
    firstname: string,
    surname: string | null,
    lastname: string,
    birthday: string | null,
    hide_email: boolean,
    hide_fio: boolean,
    hide_phone: boolean,
    hide_birthday: boolean,
    nickid: string | null,
    subscription: boolean,
}

export interface UserProfileMinimalWithOwner extends UserProfileMinimal {
    owner: number,
}

export interface UserProfileAccount extends UserProfileMinimal {
    user_id: number,
    account_id: number,
    owner_id: number,
    nickid: string | null,
    roles: {
        id: number,
        name: string
    }[]
}

export interface UserProfile extends UserProfileMinimal {
    username: string,
    lang: Language
    avatar: string | null,
    roles: Role[],
    is_active: boolean,
    date_joined: string | null,
    last_login: string | null,
    guestsince: string | null,
    pd_agreement: boolean,
    otp_enabled: boolean,
    otp_configured: boolean,
    accounts: Omit<AccountShort, 'logo'>[],
}

export interface UserResponse extends UserProfile {
    id: number,
    lang: Language,
    is_active: boolean,
    is_locked: boolean,
    lockmsg: string | null,
    date_joined: string,
    last_login: string,
    // owner: AccountListElement,
}

export interface UserProfileResponse extends UserProfile {
    id: number,
    lang: Language,
    avatar: string,
    pd_agreement: boolean,
    is_active: boolean,
    date_joined: string,
    last_login: string
    roles: Role[]
}

export interface UserResponseFull extends UserResponse {
    accounts_for_roles: number[],
    is_active: boolean,
    is_locked: boolean,
    lockmsg: string | null,
    date_joined: string,
    last_login: string,
    accounts: {
        id: number,
        rtype: number,
        name: string,
        nickname: string | null,
        org: OrgMinimal | null,
        owner: UserProfileMinimal | null,
        logo: string | null,
        roles: {
            id: number,
            name: string
        }[]
    }[]
}

export interface UserRequest {
    id?: number,
    email: string,
    username?: string,
    password?: string,
    activate?: boolean,

    lang: Language,
    send_emails?: boolean,
    emails?: string[],
    send_sms?: boolean,
    phones?: string[],
    phone: string,
    lastname: string,
    firstname: string,
    surname: string | null,
    birthday?: string | null,
    avatar: string | null,
    hide_fio: boolean,
    hide_phone: boolean,
    hide_email: boolean,
    hide_birthday?: boolean,
    account?: number,
    owner?: number,
    is_active?: boolean,
    is_locked?: boolean,
    otp_enabled?: boolean,
    otp_configured?: boolean,
    lockmsg?: string,
    request_activation?: boolean,
    reset_roles?: boolean,
    roles?: number[],
    pd_agreement?: boolean
    nickid?: string,
    subscription?: boolean,
}

export interface TotalUserData {
    all: number,
    new: number,
    offers: number,
    favorites: number,
    deals: number,
    blocked: number
}

export interface ChangePasswordRequest {
    old_password: string,
    password: string
}

export interface ConfirmTokenRequest {
    uid: string,
    token: string,
    password: string,
    otptoken?: string,
}

export interface ConfirmRequest {
    email: string,
    code: string
}

export interface ConfimResponse {
    email: string,
    email_used: boolean,
    email_verification: string,
    confirmed: boolean,
    token: string,
}

export interface EmailRequest {
    email: string,
    lang: Language,
    token?: string
}

export interface ValidateEmailResponse {
    email: string,
    resend_in: number,
    code_expired_in: number,
    sended: boolean
}

export interface ValidateEmailRequest {
    uid: string,
    token: string,
}

export interface UserAttach {
    id?: number,
    user: number,
    nickid?: string | null,
    roles: number[]
}

export interface AccountAttach {
    id?: number,
    account: number,
    nickid: string | null,
    roles: number[]
}

const staggeredBaseQuery = retry(baseQueryWithReauth, {
    maxRetries: 0,
})

export const userApiV1 = createApi({
    reducerPath: 'userApiV1',
    baseQuery: staggeredBaseQuery,
    tagTypes: ['Accounts', 'Org', 'Profile', 'Reg', 'Roles', 'Users', 'Yell'],
    keepUnusedDataFor: 30,
    endpoints: (builder) => ({
        getAccountNotifications: builder.query<StatusResponse<{ access_requests: boolean, offers?: boolean }>, number>({
            query: (id) => `be/api/v1/account/${id}/notifications/`,
        }),
        setAccountNotifications: builder.mutation<StatusResponse<{ access_requests: boolean, offers?: boolean }>, { id: number, access_requests: boolean, offers?: boolean }>({
            query: (data) => ({
                url: `be/api/v1/account/${data.id}/notifications/`,
                method: 'POST',
                body: data
            })
        }),
        getCurrentAccount: builder.query<StatusResponse<AccountResponse>, void>({
            query: () => 'be/api/v1/user/account/',
            providesTags: () => [{ type: 'Accounts' }]
        }),
        getCurrentAccountTariff: builder.query<StatusResponse<{
            tariffs: TariffAccount[],
            options: TariffAccountOption[],
            tasks: TariffTask[],
        }[]>, void>({
            query: () => 'be/api/v1/user/account/tariff/',
            // providesTags: () => [{ type: 'Accounts' }]
        }),
        getCurrentAccountTariffHistory: builder.query<StatusResponse<TariffHistory[]>, void>({
            query: () => 'be/api/v1/user/account/tariff/history/',
            // providesTags: () => [{ type: 'Accounts' }]
        }),
        getAccountList: builder.query<StatusPagesResponse<AccountUnit> & { results: { totals: TotalUserData } }, { [key: string]: string | number }>({
            query: (params) => `be/api/v1/user/account/clients/?${Object.keys(params).filter(k => params[k] || params[k] === 0).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`,
            providesTags: () => [{ type: 'Accounts' }]
        }),
        getClientAccount: builder.query<StatusResponse<AccountClientUnit>, number>({
            query: (id) => `be/api/v1/user/account/clients/${id}/`,
            providesTags: (result, err, id) => [{ type: 'Accounts', id }]
        }),
        getAccountGroups: builder.query<StatusResponse<AclGroup[]>, number>({
            query: (accountId) => `be/api/v1/account/${accountId}/aclgroups/`,
            providesTags: () => [{ type: 'Accounts' }]
        }),
        accountGroupSet: builder.mutation<StatusResponse<AclGroup>, { accountId: number, groupId: number, members: number[] }>({
            query: (data) => ({
                url: `be/api/v1/account/${data.accountId}/aclgroups/${data.groupId}/`,
                method: 'PUT',
                body: { accounts: data.members }
            }),
            invalidatesTags: [{ type: 'Accounts' }],
        }),
        getAccountOrganizations: builder.query<StatusPagesResponse<OrganizationShort>, OffsetLimit>({
            query: (params) => `be/api/v1/user/account/organizations/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`,
            providesTags: () => [{ type: 'Accounts' }]
        }),
        getAccountTags: builder.query<StatusPagesResponse<TagFull>, OffsetLimit>({
            query: (params) => `be/api/v1/user/account/tags/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`,
            providesTags: () => [{ type: 'Accounts' }]
        }),
        getAccountTag: builder.query<StatusResponse<TagFull>, number>({
            query: (id) => `be/api/v1/user/account/tags/${id}/`,
            providesTags: () => [{ type: 'Accounts' }]
        }),
        getAccountDataplan: builder.query<StatusResponse<Dataplan[]>, void>({
            query: () => 'be/api/v1/user/account/dataplan/',
            providesTags: () => [{ type: 'Accounts' }]
        }),
        getAccountLogin: builder.query<StatusResponse<AccountLogin[]>, void>({
            query: () => 'be/api/v1/user/account/login/',
            providesTags: () => [{ type: 'Accounts' }]
        }),
        getAccountRoles: builder.query<StatusResponse<(Role & { can_assign: boolean })[]>, void>({
            query: () => 'be/api/v1/user/account/roles/',
            providesTags: () => [{ type: 'Accounts' }]
        }),
        getUserAccountsManage: builder.query<StatusPagesResponse<AccountAvail>, void>({
            query: () => 'be/api/v1/user/account/managed/?offset=0&limit=-1',
            providesTags: () => [{ type: 'Accounts' }]
        }),

        getAccounts: builder.query<StatusPagesResponse<AccountListElement>, OffsetLimit>({
            query: (params) => `be/api/v1/account/?${Object.keys(params).filter(k => params[k] || params[k] === 0).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`,
            providesTags: () => [{ type: 'Accounts' }],
            serializeQueryArgs: ({ queryArgs, endpointName }) => {
                const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
                return end;
            },
            // // Always merge incoming data to the cache entry
            merge: (currentCache, newItems, otherArgs) => {
                if (otherArgs.arg.offset === 0) {
                    currentCache.results.data = newItems.results.data;
                } else {
                    currentCache.results.data.push(...newItems.results.data)
                }
            },
            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        getAccount: builder.query<StatusResponse<AccountResponse>, number | string>({
            query: (id: number | string) => {
                return `be/api/v1/account/${id}/`
            }
        }),
        createAccount: builder.mutation<StatusResponse<AccountResponse>, AccountRequest>({
            query: (data) => ({
                url: 'be/api/v1/account/',
                method: 'POST',
                body: data
            }),
            invalidatesTags: [{ type: 'Accounts' }]
        }),
        getAccountAgreement: builder.query<StatusResponse<{ agr_offer: boolean | null }>, void>({
            query: () => 'be/api/v1/account/agreement/'
        }),
        updateAccountAgreement: builder.mutation<StatusResponse<AccountResponse>, void>({
            query: () => ({
                url: 'be/api/v1/account/agreement/',
                method: 'POST',
                body: { agr_offer: true }
            }),
        }),
        updateAccount: builder.mutation<StatusResponse<AccountResponse>, Partial<AccountUpdateRequest>>({
            query: (data) => ({
                url: `be/api/v1/account/${data.id}/`,
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: [{ type: 'Accounts' }]
        }),
        deleteAccount: builder.mutation<void, number>({
            query: (id: number) => ({
                url: `be/api/v1/account/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Accounts' }]
        }),
        // Тарифы
        getAccountTariffs: builder.query<StatusResponse<TariffAccountShort>, number>({
            query: (account_id) => `be/api/v1/account/${account_id}/tariff/tariffs/`
        }),
        updateAccountTariffs: builder.mutation<StatusResponse<TariffTask>, { account_id: number, tariff_id: number, etime?: string }>({
            query: ({ account_id, tariff_id, etime }) => ({
                url: `be/api/v1/account/${account_id}/tariff/tasks/`,
                method: 'POST',
                body: { tariff: tariff_id, etime }
            }),
        }),
        deleteAccountTariffs: builder.mutation<StatusResponse<TariffAccountShort>, { account_id: number, task_id: number }>({
            query: ({ account_id, task_id }) => ({
                url: `be/api/v1/account/${account_id}/tariff/tasks/${task_id}/`,
                method: 'DELETE',
            }),
        }),
        getAccountTariff: builder.query<StatusResponse<Pick<AccountResponse, 'tariffs'>>, number>({
            query: (account_id) => `be/api/v1/account/${account_id}/tariff/`,
            // providesTags: () => [{ type: 'Accounts' }]
        }),
        getAccountTariffHistory: builder.query<StatusResponse<TariffHistory[]>, number>({
            query: (account_id) => `be/api/v1/account/${account_id}/tariff/history/`,
            // providesTags: () => [{ type: 'Accounts' }]
        }),
        // updateAccountTariffs: builder.mutation<StatusResponse<TariffAccountShort>, { account_id: number, tariff_id: number, validity?: { lower?: string, upper?: string } }>({
        //     query: ({ account_id, tariff_id, validity }) => ({
        //         url: `be/api/v1/account/${account_id}/tariff/tariffs/`,
        //         method: 'POST',
        //         body: { tariff: tariff_id, validity }
        //     }),
        // }),
        // deleteAccountTariffs: builder.mutation<StatusResponse<TariffAccountShort>, { account_id: number, tariff_id: number }>({
        //     query: ({ account_id, tariff_id }) => ({
        //         url: `be/api/v1/account/${account_id}/tariff/tariffs/${tariff_id}/`,
        //         method: 'DELETE',
        //     }),
        // }),

        getAccountOptions: builder.query<StatusResponse<TariffAccountShort>, number>({
            query: (account_id) => `be/api/v1/account/${account_id}/tariff/options/`
        }),
        createAccountOption: builder.mutation<StatusResponse<TariffTask>, { account_id: number, option_id: number, etime?: string }>({
            query: ({ account_id, option_id, etime }) => ({
                url: `be/api/v1/account/${account_id}/tariff/tasks/`,
                method: 'POST',
                body: { option: option_id, etime }
            }),
        }),
        // updateAccountOptions: builder.mutation<StatusResponse<TariffAccountShort>, { account_id: number, option_id: number, validity?: { lower?: string, upper?: string } }>({
        //     query: ({ account_id, option_id, validity }) => ({
        //         url: `be/api/v1/account/${account_id}/tariff/options/`,
        //         method: 'POST',
        //         body: { option: option_id, validity }
        //     }),
        // }),
        deleteAccountOptions: builder.mutation<StatusResponse<TariffAccountShort>, { account_id: number, option_id: number }>({
            query: ({ account_id, option_id }) => ({
                url: `be/api/v1/account/${account_id}/tariff/options/${option_id}/`,
                method: 'DELETE',
            }),
        }),
        deleteDefferedAccountOptions: builder.mutation<StatusResponse<TariffAccountShort>, { account_id: number, task_id: number }>({
            query: ({ account_id, task_id }) => ({
                url: `be/api/v1/account/${account_id}/tariff/tasks/${task_id}/`,
                method: 'DELETE',
            }),
        }),

        createAccountLogo: builder.mutation<void, { accountId: number, file: File, onProgress?: (e: AxiosProgressEvent) => void }>({
            query(data) {
                const formData = new FormData();
                formData.append('file', data.file);
                return {
                    url: `be/api/v1/account/${data.accountId}/logo/`,
                    method: 'PUT',
                    body: formData,
                    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
                        data.onProgress && data.onProgress(progressEvent)
                    },
                };
            },
            invalidatesTags: [{ type: 'Users' }]
        }),
        deleteAccountLogo: builder.mutation<void, number>({
            query: (accountId) => ({
                url: `be/api/v1/account/${accountId}/logo/`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Users' }]
        }),

        getAccountsTags: builder.query<StatusPagesResponse<TagListElement>, OffsetLimit>({
            query: (params) => `be/api/v1/account/tags/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`,
            providesTags: () => [{ type: 'Accounts' }],
            serializeQueryArgs: ({ queryArgs, endpointName }) => {
                const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
                return end;
            },
            // // Always merge incoming data to the cache entry
            merge: (currentCache, newItems, otherArgs) => {
                if (otherArgs.arg.offset === 0) {
                    currentCache.results.data = newItems.results.data;
                } else {
                    currentCache.results.data.push(...newItems.results.data)
                }
            },
            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        getAccountsTag: builder.query<StatusResponse<TagListElement>, number>({
            query: (tagId) => `be/api/v1/account/tags/${tagId}/`,
            providesTags: () => [{ type: 'Accounts' }]
        }),
        createAccoutTag: builder.mutation<StatusResponse<Tag[]>, TagRequest>({
            query: (data) => ({
                url: 'be/api/v1/account/tags/',
                method: 'POST',
                body: data
            }),
            invalidatesTags: [{ type: 'Accounts' }]
        }),
        updateAccountTag: builder.mutation<StatusResponse<Tag[]>, Partial<TagRequest>>({
            query: (data) => ({
                url: `be/api/v1/account/tags/${data.id}/`,
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: [{ type: 'Accounts' }]
        }),
        setAccountTagControl: builder.mutation<StatusResponse<Tag[]>, { id: number, control: number | null }>({
            query: (data) => ({
                url: `be/api/v1/account/tags/${data.id}/`,
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: [{ type: 'Accounts' }]
        }),
        deleteAccountTag: builder.mutation<void, number>({
            query: (id: number) => ({
                url: `be/api/v1/account/tags/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Accounts' }]
        }),
        getAccountsOrganizations: builder.query<StatusPagesResponse<Organization>, OffsetLimit>({
            query: (params) => `be/api/v1/account/organizations/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`,
            providesTags: () => [{ type: 'Org' }],
            serializeQueryArgs: ({ queryArgs, endpointName }) => {
                const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
                return end;
            },
            // // Always merge incoming data to the cache entry
            merge: (currentCache, newItems, otherArgs) => {
                if (otherArgs.arg.offset === 0) {
                    currentCache.results.data = newItems.results.data;
                } else {
                    currentCache.results.data.push(...newItems.results.data)
                }
            },
            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),

        getOrganizationDocuments: builder.query<StatusPagesResponse<{
            id: number,
            terms: {
                ru: string,
                en: string
            },
            rank: number,
            warn: boolean
        }>, OffsetLimit>({
            query: (params) => `be/api/v1/dictionaries/mbcontract/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`,
        }),
        getOrganization: builder.query<StatusResponse<OrganizationFull>, number | string>({
            query: (id: number | string) => {
                return `be/api/v1/account/organizations/${id}/`
            },
            providesTags: (result, error, id) => [{ type: 'Org', id }],
        }),
        createOrganization: builder.mutation<StatusResponse<Organization>, Omit<Organization, 'id'>>({
            query: (data) => ({
                url: 'be/api/v1/account/organizations/',
                method: 'POST',
                body: data
            }),
            invalidatesTags: [{ type: 'Org' }]
        }),
        updateOrganization: builder.mutation<StatusResponse<Organization>, Partial<Organization>>({
            query: (data) => ({
                url: `be/api/v1/account/organizations/${data.id}/`,
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: [{ type: 'Org' }]
        }),
        deleteOrganization: builder.mutation<void, number>({
            query: (id: number) => ({
                url: `be/api/v1/account/organizations/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Org' }]
        }),

        getUserProfile: builder.query<StatusResponse<UserProfile>, void>({
            query: () => 'be/api/v1/user/profile/',
            providesTags: () => [{ type: 'Profile' }]
        }),
        updateUserProfile: builder.mutation<StatusResponse<UserProfile>, Partial<UserProfile>>({
            query: (data: UserProfile) => ({
                url: 'be/api/v1/user/profile/',
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: [{ type: 'Profile' }],
        }),
        updateUserProfileAgreement: builder.mutation<StatusResponse<UserProfile>, void>({
            query: () => ({
                url: 'be/api/v1/user/profile/',
                method: 'PATCH',
                body: { pd_agreement: true }
            }),
            invalidatesTags: [{ type: 'Profile' }],
        }),

        getUsers: builder.query<StatusPagesResponse<UserResponse>, OffsetLimit>({
            query: (params) => `be/api/v1/user/?${Object.keys(params).filter(k => params[k] || params[k] === 0).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`,
            providesTags: () => [{ type: 'Users' }],
            serializeQueryArgs: ({ queryArgs, endpointName }) => {
                const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
                return end;
            },
            // // Always merge incoming data to the cache entry
            merge: (currentCache, newItems, otherArgs) => {
                if (otherArgs.arg.offset === 0) {
                    currentCache.results.data = newItems.results.data;
                } else {
                    currentCache.results.data.push(...newItems.results.data)
                }
            },
            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        getUser: builder.query<StatusResponse<UserResponseFull>, number | string>({
            query: (id: number | string) => {
                return `be/api/v1/user/${id}/`
            },
            providesTags: (result, error, id) => [{ type: 'Users', id }],
        }),
        deleteUser: builder.mutation<void, number>({
            query: (user_id) => ({
                url: `be/api/v1/user/${user_id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Users' }]
        }),
        createUser: builder.mutation<StatusResponse<UserResponse>, UserRequest>({
            query: (data) => ({
                url: 'be/api/v1/user/',
                method: 'POST',
                body: data
            }),
            invalidatesTags: [{ type: 'Users' }]
        }),
        updateUser: builder.mutation<StatusResponse<UserResponse>, Partial<UserRequest>>({
            query: (data: UserRequest) => ({
                url: `be/api/v1/user/${data.id}/`,
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Users' }, { type: 'Users', id }],
        }),

        getAllRoles: builder.query<StatusResponse<{ rtype: number, roles: (Role & { can_assign: boolean })[] }[]>, void>({
            query: () => 'be/api/v1/user/account/roles/all/',
            providesTags: () => [{ type: 'Roles' }]
        }),
        getUserAccountsAvailable: builder.query<StatusResponse<AccountAvail[]>, number | string>({
            query: (id) => `be/api/v1/user/${id}/accounts/available/`,
            providesTags: () => [{ type: 'Accounts' }]
        }),

        attachUserToAccount: builder.mutation<StatusResponse<UserAttach>, UserAttach>({
            query: (data) => ({
                url: `be/api/v1/account/${data.id}/users/attach/`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: [{ type: 'Users' }],
        }),
        detachUserFromAccount: builder.mutation<StatusResponse<{ user: number }>, { id: number, user: number }>({
            query: (data) => ({
                url: `be/api/v1/account/${data.id}/users/detouch/`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: (result, error, data) => [{ type: 'Users', id: data.user }]
        }),

        attachAccountToUser: builder.mutation<StatusResponse<AccountAttach>, AccountAttach>({
            query: (data) => ({
                url: `be/api/v1/user/${data.id}/users/attach/`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: [{ type: 'Users' }],
        }),
        detachAccountFromUser: builder.mutation<StatusResponse<{ account: number }>, { id: number, account: number }>({
            query: (data) => ({
                url: `be/api/v1/user/${data.id}/accounts/detouch/`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: [{ type: 'Accounts' }],
        }),

        getAccountUsersAvailable: builder.query<StatusResponse<UserProfileMinimalWithOwner[]>, number | string>({
            query: (id) => `be/api/v1/account/${id}/users/available/`,
            providesTags: () => [{ type: 'Users' }]
        }),

        validateEmail: builder.mutation<StatusResponse<ValidateEmailResponse>, EmailRequest>({
            query: (data: EmailRequest) => ({
                url: 'be/api/v1/registration/email/validate/',
                method: 'POST',
                body: data
            })
        }),
        confirmEmail: builder.mutation<StatusResponse<ConfimResponse>, ConfirmRequest>({
            query: (data: ConfirmRequest) => ({
                url: 'be/api/v1/registration/email/confirm/',
                method: 'POST',
                body: data
            })
        }),
        getRegistrations: builder.query<StatusResponse<Registration[]>, void>({
            query: () => 'be/api/v1/registration/',
            providesTags: () => [{ type: 'Reg' }]
        }),
        getRegistration: builder.query<StatusResponse<Registration>, number>({
            query: (id: number) => `be/api/v1/registration/${id}/`,
            providesTags: (result, error, id) => [{ type: 'Reg', id }],
        }),
        createRegistration: builder.mutation<StatusResponse<Registration>, RegistrationRequest>({
            query: (data) => ({
                url: 'be/api/v1/registration/',
                method: 'POST',
                body: data
            }),
            invalidatesTags: [{ type: 'Reg' }]
        }),
        updateRegistration: builder.mutation<StatusResponse<Registration>, Partial<Registration>>({
            query: (data) => ({
                url: `be/api/v1/registration/${data.id}/`,
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: (result, error, data) => [{ type: 'Reg', id: data.id }]
        }),
        deleteRegistration: builder.mutation<void, number>({
            query: (id) => ({
                url: `be/api/v1/registration/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Reg' }]
        }),
        userApprove: builder.mutation<StatusResponse<Registration[]>, { id: number, data: ApproveRequest }>({
            query: ({ id, data }) => ({
                url: `be/api/v1/registration/${id}/approve/`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: () => [{ type: 'Reg' }]
        }),
        // for users.ts
        getProfile: builder.query<StatusResponse<UserProfileResponse> & { ts?: string }, string | undefined>({
            query: () => 'be/api/v1/user/profile/',
            providesTags: () => [{ type: 'Profile' }],
            transformResponse: (response: StatusResponse<UserProfileResponse>, meta, arg) => {
                const result = { ...response, ts: arg };
                return result;
            },
        }),
        getOTP: builder.query<StatusResponse<OTPStatus>, void>({
            query: () => 'be/api/v1/user/profile/otp/',
            providesTags: () => [{ type: 'Profile' }],
        }),
        registerOTP: builder.mutation<StatusResponse<OTPResponse>, void>({
            query: () => ({
                url: 'be/api/v1/user/profile/otp/',
                method: 'POST',
            })
        }),
        resetOTP: builder.mutation<void, void>({
            query: () => ({
                url: 'be/api/v1/user/profile/otp/reset/',
                method: 'POST',
            })
        }),
        verifyOTP: builder.mutation<void, { token: string }>({
            query: (data) => ({
                url: 'be/api/v1/user/profile/otp/verify/',
                method: 'POST',
                body: data
            })
        }),
        confirmPassword: builder.mutation<StatusResponse<null>, ConfirmTokenRequest>({
            query: (data: ConfirmTokenRequest) => ({
                url: 'be/api/v1/user/profile/password/reset/confirm/',
                method: 'POST',
                body: data
            })
        }),
        confirmActivation: builder.mutation<StatusResponse<null>, ConfirmTokenRequest>({
            query: (data: ConfirmTokenRequest) => ({
                url: 'be/api/v1/user/profile/activation/confirm/',
                method: 'POST',
                body: data
            })
        }),
        resetPassword: builder.mutation<StatusResponse<null>, number>({
            query: (id: number) => ({
                url: `be/api/v1/user/${id}/password/reset/`,
                method: 'POST',
                body: { inactivate: true }
            })
        }),
        resetProfilePassword: builder.mutation<StatusResponse<null>, { account: string, token: string }>({
            query: (data) => ({
                url: 'be/api/v1/user/profile/password/reset/',
                method: 'POST',
                body: { account: data.account, token: data.token }
            })
        }),
        setProfilePassword: builder.mutation<StatusResponse<null>, ChangePasswordRequest>({
            query: (data: ChangePasswordRequest) => ({
                url: 'be/api/v1/user/profile/password/set/',
                method: 'POST',
                body: data
            })
        }),
        validateActivation: builder.mutation<StatusResponse<void>, ValidateEmailRequest>({
            query: (data: ValidateEmailRequest) => ({
                url: 'be/api/v1/user/profile/activation/validate/',
                method: 'POST',
                body: data
            })
        }),
        validateChangePassword: builder.mutation<StatusResponse<void>, ValidateEmailRequest>({
            query: (data: ValidateEmailRequest) => ({
                url: 'be/api/v1/user/profile/password/reset/validate-token/',
                method: 'POST',
                body: data
            })
        }),
        createUserAvatar: builder.mutation<void, { file: File, onProgress?: (e: AxiosProgressEvent) => void }>({
            query(data) {
                const formData = new FormData();
                formData.append('file', data.file);
                return {
                    url: 'be/api/v1/user/profile/avatar/',
                    method: 'PUT',
                    body: formData,
                    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
                        data.onProgress && data.onProgress(progressEvent)
                    },
                };
            },
            invalidatesTags: [{ type: 'Users' }]
        }),
        deleteUserAvatar: builder.mutation<void, void>({
            query: () => ({
                url: 'be/api/v1/user/profile/avatar/',
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Users' }]
        }),
        getTariffs: builder.query<StatusResponse<Tariff[]>, void>({
            query: () => {
                return 'be/api/v1/tariffs/'
            },
        }),
        getTariffsOptions: builder.query<StatusResponse<TariffOption[]>, void>({
            query: () => {
                return 'be/api/v1/tariffs/options/'
            },
        }),
        getCountries: builder.query<StatusPagesResponse<Country>, void>({
            query: () => {
                return 'be/api/v1/dictionaries/country/?limit=-1'
            },
        }),
        getTVDBAccounts: builder.query<StatusResponse<TVDBAccount[]>, void>({
            query: () => {
                return 'be/api/v1/tvdb/yell/accounts/'
            },
            providesTags: [{ type: 'Yell' }],
        }),
        getTVDBAccount: builder.query<StatusResponse<TVDBAccount>, number>({
            query: (id) => {
                return `be/api/v1/tvdb/yell/accounts/${id}/`
            },
        }),
        createTVDBAccount: builder.mutation<StatusResponse<TVDBAccount>, TVDBAccountRequest>({
            query: (data) => ({
                url: 'be/api/v1/tvdb/yell/accounts/',
                method: 'POST',
                body: data
            }),
            invalidatesTags: [{ type: 'Yell' }]
        }),
        updateTVDBAccount: builder.mutation<StatusResponse<TVDBAccount>, Partial<TVDBAccountRequest>>({
            query: (data) => ({
                url: `be/api/v1/tvdb/yell/accounts/${data.id}/`,
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: [{ type: 'Yell' }]
        }),
        deleteTVDBAccount: builder.mutation<void, number>({
            query: (id: number) => ({
                url: `be/api/v1/tvdb/yell/accounts/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Yell' }]
        }),
    }),
})

export const {
    useAccountGroupSetMutation,
    useAttachAccountToUserMutation,
    useAttachUserToAccountMutation,
    useConfirmActivationMutation,
    useConfirmEmailMutation,
    useConfirmPasswordMutation,
    useCreateAccountLogoMutation,
    useCreateAccountMutation,
    useCreateAccountOptionMutation,
    useCreateAccoutTagMutation,
    useCreateOrganizationMutation,
    useCreateRegistrationMutation,
    useCreateTVDBAccountMutation,
    useCreateUserAvatarMutation,
    useCreateUserMutation,
    useDeleteAccountLogoMutation,
    useDeleteAccountMutation,
    useDeleteAccountOptionsMutation,
    useDeleteAccountTagMutation,
    useDeleteAccountTariffsMutation,
    useDeleteDefferedAccountOptionsMutation,
    useDeleteOrganizationMutation,
    useDeleteRegistrationMutation,
    useDeleteTVDBAccountMutation,
    useDeleteUserAvatarMutation,
    useDeleteUserMutation,
    useDetachAccountFromUserMutation,
    useDetachUserFromAccountMutation,
    useGetAccountAgreementQuery,
    useGetAccountDataplanQuery,
    useGetAccountGroupsQuery,
    useGetAccountListQuery,
    useGetAccountLoginQuery,
    useGetAccountNotificationsQuery,
    useGetAccountOptionsQuery,
    useGetAccountOrganizationsQuery,
    useGetAccountQuery,
    useGetAccountRolesQuery,
    useGetAccountsOrganizationsQuery,
    useGetAccountsQuery,
    useGetAccountsTagQuery,
    useGetAccountsTagsQuery,
    useGetAccountTagQuery,
    useGetAccountTagsQuery,
    useGetAccountTariffHistoryQuery,
    useGetAccountTariffQuery,
    useGetAccountTariffsQuery,
    useGetAccountUsersAvailableQuery,
    useGetAllRolesQuery,
    useGetClientAccountQuery,
    useGetCountriesQuery,
    useGetCurrentAccountQuery,
    useGetCurrentAccountTariffHistoryQuery,
    useGetCurrentAccountTariffQuery,
    useGetOrganizationQuery,
    useGetOTPQuery,
    useGetProfileQuery,
    useGetRegistrationQuery,
    useGetRegistrationsQuery,
    useGetTariffsOptionsQuery,
    useGetTariffsQuery,
    useGetTVDBAccountQuery,
    useGetTVDBAccountsQuery,
    useGetUserAccountsAvailableQuery,
    useGetUserAccountsManageQuery,
    useGetUserProfileQuery,
    useGetUserQuery,
    useGetUsersQuery,
    useLazyGetAccountAgreementQuery,
    useLazyGetAccountDataplanQuery,
    useLazyGetAccountGroupsQuery,
    useLazyGetAccountListQuery,
    useLazyGetAccountLoginQuery,
    useLazyGetAccountNotificationsQuery,
    useLazyGetAccountOptionsQuery,
    useLazyGetAccountOrganizationsQuery,
    useLazyGetAccountQuery,
    useLazyGetAccountRolesQuery,
    useLazyGetAccountsOrganizationsQuery,
    useLazyGetAccountsQuery,
    useLazyGetAccountsTagQuery,
    useLazyGetAccountsTagsQuery,
    useLazyGetAccountTagQuery,
    useLazyGetAccountTagsQuery,
    useLazyGetAccountTariffHistoryQuery,
    useLazyGetAccountTariffQuery,
    useLazyGetAccountTariffsQuery,
    useLazyGetAccountUsersAvailableQuery,
    useLazyGetAllRolesQuery,
    useLazyGetClientAccountQuery,
    useLazyGetCountriesQuery,
    useLazyGetCurrentAccountQuery,
    useLazyGetCurrentAccountTariffHistoryQuery,
    useLazyGetCurrentAccountTariffQuery,
    useLazyGetOrganizationQuery,
    useLazyGetOTPQuery,
    useLazyGetProfileQuery,
    useLazyGetRegistrationQuery,
    useLazyGetRegistrationsQuery,
    useLazyGetTariffsOptionsQuery,
    useLazyGetTariffsQuery,
    useLazyGetTVDBAccountQuery,
    useLazyGetTVDBAccountsQuery,
    useLazyGetUserAccountsAvailableQuery,
    useLazyGetUserAccountsManageQuery,
    useLazyGetUserProfileQuery,
    useLazyGetUserQuery,
    useLazyGetUsersQuery,
    useRegisterOTPMutation,
    useResetOTPMutation,
    useResetPasswordMutation,
    useResetProfilePasswordMutation,
    useSetAccountNotificationsMutation,
    useSetAccountTagControlMutation,
    useSetProfilePasswordMutation,
    useUpdateAccountAgreementMutation,
    useUpdateAccountMutation,
    useUpdateAccountTagMutation,
    useUpdateAccountTariffsMutation,
    useUpdateOrganizationMutation,
    useUpdateRegistrationMutation,
    useUpdateTVDBAccountMutation,
    useUpdateUserMutation,
    useUpdateUserProfileAgreementMutation,
    useUpdateUserProfileMutation,
    useUserApproveMutation,
    useValidateActivationMutation,
    useValidateChangePasswordMutation,
    useValidateEmailMutation,
    useVerifyOTPMutation,
    useGetOrganizationDocumentsQuery,
    useLazyGetOrganizationDocumentsQuery,
} = userApiV1;
